import type { ReactNode, JSX} from "react";
import { PaneFrame } from "./PaneBlocks";
import { LEFT_RAIL_WIDTH } from "./layoutMetrics";
import { StatusBar } from "./StatusBar";
import { CommandBar } from "./CommandBar";
import { useUiStore } from "../../store/uiStore";

export interface AppShellProps {
  readonly leftRail: ReactNode;
  readonly children: ReactNode;
}

/**
 * Root application shell with left rail and main workspace.
 *
 * Window dragging is OPT-IN from chrome only — the CommandBar title bar
 * (`data-tauri-drag-region`) and each PaneHeaderBar. The root MUST NOT attach a
 * whole-screen onMouseDown→startWindowDrag handler: it made every non-interactive
 * element (message text, labels, scroll areas) drag the window, so the user
 * couldn't select text or click in the agent dialog and content panes.
 */
export function AppShell({ leftRail, children }: AppShellProps): JSX.Element {
  const statusBarVisible = useUiStore((s) => s.statusBarVisible);

  return (
    <div
      className="relative flex flex-col h-screen w-screen overflow-hidden bg-surface-tertiary"
    >
      <CommandBar />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <PaneFrame
          tone="surface-tertiary"
          className="shrink-0 items-center"
          style={{
            width: LEFT_RAIL_WIDTH,
            minWidth: LEFT_RAIL_WIDTH,
            maxWidth: LEFT_RAIL_WIDTH,
            flexBasis: LEFT_RAIL_WIDTH,
          }}
          withRightBorder
        >
          {leftRail}
        </PaneFrame>
        <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden bg-surface-tertiary">
          {children}
        </div>
      </div>
      {statusBarVisible && <StatusBar />}
    </div>
  );
}
